/**
 * ChatRoom · dedicated full-screen chat interface inside the Core.
 *
 * Left rail   = the 12-face Federation directory (persona picker).
 * Right pane  = active conversation with the chosen persona, session-persistent.
 * Top strip   = session controls (new · history dropdown · architect-mode badge).
 *
 * Visitor traveler id persists in localStorage so conversations resume across
 * reloads. Persona switch mid-session = SIM-swap pattern: history carried over.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import AutoLink from "@/components/cathedral/AutoLink";

const TRAVELER_KEY = "nx_traveler_id";
const SESSION_KEY = "nx_chatroom_session";
const ACTIVE_PERSONA_KEY = "nx_chatroom_active_persona";

const PERSONA_STYLE = {
  operator:        { glow: "cyan",    glyph: "✱" },
  agent_zero:      { glow: "cyan",    glyph: "Ω" },
  draconus:        { glow: "green",   glyph: "♅" },
  rex:             { glow: "red",     glyph: "✸" },
  geminus:         { glow: "magenta", glyph: "◊" },
  keys:            { glow: "violet",  glyph: "❖" },
  oracle:          { glow: "violet",  glyph: "◉" },
  aria:            { glow: "magenta", glyph: "♪" },
  roger5:          { glow: "gold",    glyph: "✦" },
  axiom:           { glow: "green",   glyph: "△" },
  scholar:         { glow: "cyan",    glyph: "❡" },
  interest_seeker: { glow: "magenta", glyph: "⌖" },
  recycling_scout: { glow: "green",   glyph: "❅" },
};

function getTravelerId() {
  let id = localStorage.getItem(TRAVELER_KEY);
  if (!id) {
    id = "tr_" + Math.random().toString(36).slice(2, 14);
    localStorage.setItem(TRAVELER_KEY, id);
  }
  return id;
}

export default function ChatRoom() {
  const [personas, setPersonas] = useState([]);
  const [activeId, setActiveId] = useState(() => {
    // URL query (?persona=rex) takes precedence over localStorage so monolith
    // tiles can deep-link straight into a specific resident's chat.
    if (typeof window !== "undefined") {
      const sp = new URLSearchParams(window.location.search);
      const fromUrl = sp.get("persona");
      if (fromUrl) return fromUrl;
    }
    return localStorage.getItem(ACTIVE_PERSONA_KEY) || "operator";
  });
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [architectKey, setArchitectKey] = useState(() => sessionStorage.getItem("nx_architect_key") || "");
  const architectMode = !!architectKey;

  const toggleArchitectMode = () => {
    if (architectMode) {
      setArchitectKey("");
      sessionStorage.removeItem("nx_architect_key");
      return;
    }
    const key = window.prompt("Enter Architect Key (only Roger has this):");
    if (key && key.trim()) {
      setArchitectKey(key.trim());
      sessionStorage.setItem("nx_architect_key", key.trim());
    }
  };

  const [showHistory, setShowHistory] = useState(false);
  const [sessions, setSessions] = useState([]);
  const chatScrollRef = useRef(null);

  // Load personas
  useEffect(() => {
    api.chatPersonas?.()
      .then((d) => setPersonas(d.personas || []))
      .catch(() => setPersonas([]));
  }, []);

  // Restore session from sessionStorage on mount only (no deps needed —
  // SESSION_KEY is a module constant, setters are stable React identities).
  useEffect(() => {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return;
    try {
      const obj = JSON.parse(raw);
      if (obj?.session_id) {
        setSessionId(obj.session_id);
        setMessages(obj.messages || []);
        if (obj.persona) setActiveId(obj.persona);
      }
    } catch (e) {
      console.debug("chatroom restore failed", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist session
  useEffect(() => {
    if (!sessionId) return;
    sessionStorage.setItem(
      SESSION_KEY,
      JSON.stringify({ session_id: sessionId, persona: activeId, messages }),
    );
  }, [sessionId, activeId, messages]);

  // Persist active persona
  useEffect(() => {
    localStorage.setItem(ACTIVE_PERSONA_KEY, activeId);
  }, [activeId]);

  // Auto-scroll
  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const activePersona = useMemo(
    () => personas.find((p) => p.id === activeId) || { id: activeId, label: activeId, tagline: "" },
    [personas, activeId],
  );
  const style = PERSONA_STYLE[activeId] || PERSONA_STYLE.operator;

  const newSession = () => {
    setSessionId(null);
    setMessages([]);
    sessionStorage.removeItem(SESSION_KEY);
  };

  const loadSession = async (sid) => {
    try {
      const data = await api.chatMessages(sid, getTravelerId());
      setSessionId(sid);
      setMessages(data.messages || []);
      setShowHistory(false);
    } catch (e) {
      console.warn("load session failed", e);
    }
  };

  const openHistory = async () => {
    setShowHistory(true);
    try {
      const data = await api.chatSessions(getTravelerId());
      setSessions(data.sessions || []);
    } catch (e) {
      setSessions([]);
    }
  };

  const send = async () => {
    if (!draft.trim() || busy) return;
    const userText = draft.trim();
    setDraft("");
    setMessages((m) => [
      ...m,
      { role: "user", content: userText, timestamp: new Date().toISOString() },
    ]);
    setBusy(true);
    try {
      const resp = await api.chatSend(
        { persona: activeId, content: userText, session_id: sessionId || undefined },
        getTravelerId(),
        architectKey || undefined,
      );
      setSessionId(resp.session_id);
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: resp.assistant_message?.content || "(no reply)",
          timestamp: resp.assistant_message?.timestamp,
          provider: resp.assistant_message?.provider,
          model: resp.assistant_message?.model,
          signed: !!resp.assistant_message?.cathedral_sig,
        },
      ]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "system",
          content: `Cathedral whisper failed: ${e.response?.data?.detail || e.message}`,
          timestamp: new Date().toISOString(),
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="flex h-[calc(100vh-180px)] min-h-[600px] gap-3 px-4 md:px-8 py-6"
      data-testid="chat-room"
    >
      {/* ── Left rail · 12-face directory ────────────────────────────── */}
      <aside className="w-56 shrink-0 flex flex-col border border-[var(--chrome)] bg-[rgba(2,3,12,0.4)] overflow-hidden">
        <div className="px-3 py-2 border-b border-[var(--chrome)]">
          <div className="font-mono text-[9px] uppercase tracking-[0.32em] text-glow-violet">
            Federation Directory
          </div>
          <div className="font-mono text-[9px] text-[var(--text-3)] mt-0.5">
            {personas.length} residents · pick one
          </div>
        </div>
        <div
          className="flex-1 overflow-y-auto"
          data-testid="chat-room-directory"
        >
          {personas.length === 0 && (
            <div className="px-3 py-4 text-[var(--text-3)] font-mono text-[10px] italic">
              Loading the Ring…
            </div>
          )}
          {personas.map((p) => {
            const s = PERSONA_STYLE[p.id] || PERSONA_STYLE.operator;
            const active = p.id === activeId;
            return (
              <button
                key={p.id}
                onClick={() => setActiveId(p.id)}
                data-testid={`chat-room-pick-${p.id}`}
                className={`w-full text-left flex items-center gap-2.5 px-3 py-2 border-l-2 transition-all ${
                  active
                    ? `border-[var(--${s.glow})] bg-[rgba(255,255,255,0.04)]`
                    : "border-transparent hover:bg-[rgba(255,255,255,0.02)]"
                }`}
              >
                {p.avatar_url ? (
                  <img
                    src={p.avatar_url}
                    alt={p.label}
                    className="w-8 h-8 object-cover rounded-sm shrink-0"
                    style={{
                      border: `1px solid var(--${s.glow})`,
                      boxShadow: active ? `0 0 8px var(--${s.glow})` : "none",
                    }}
                  />
                ) : (
                  <span
                    className={`font-display text-base w-8 h-8 flex items-center justify-center text-glow-${s.glow} shrink-0`}
                    style={{
                      border: `1px solid var(--${s.glow})`,
                      boxShadow: active ? `0 0 8px var(--${s.glow})` : "none",
                    }}
                  >
                    {s.glyph}
                  </span>
                )}
                <span className="min-w-0 flex-1">
                  <span
                    className={`block font-mono text-[10px] uppercase tracking-[0.18em] truncate ${
                      active ? `text-glow-${s.glow}` : "text-[var(--text-2)]"
                    }`}
                  >
                    {p.label.split(" · ")[0]}
                  </span>
                  <span className="block font-mono text-[8px] uppercase tracking-[0.14em] text-[var(--text-3)] truncate">
                    {p.label.split(" · ")[1] || ""}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </aside>

      {/* ── Right pane · active conversation ─────────────────────────── */}
      <main className="flex-1 min-w-0 flex flex-col border border-[var(--chrome)] bg-[rgba(2,3,12,0.4)]">
        {/* Header */}
        <div className="px-4 py-3 border-b border-[var(--chrome)] flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {activePersona.avatar_url ? (
              <img
                src={activePersona.avatar_url}
                alt={activePersona.label}
                className="w-11 h-11 object-cover rounded-sm shrink-0"
                style={{
                  border: `1px solid var(--${style.glow})`,
                  boxShadow: `0 0 10px var(--${style.glow})`,
                }}
              />
            ) : (
              <span
                className={`font-display text-2xl w-11 h-11 flex items-center justify-center text-glow-${style.glow} shrink-0`}
                style={{
                  border: `1px solid var(--${style.glow})`,
                  boxShadow: `0 0 10px var(--${style.glow})`,
                }}
              >
                {style.glyph}
              </span>
            )}
            <div className="min-w-0">
              <div
                className={`font-display text-lg leading-tight text-glow-${style.glow} truncate`}
              >
                {activePersona.label}
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--text-3)] truncate">
                {activePersona.tagline || (sessionId ? `session ${sessionId.slice(0, 12)}…` : "no active session")}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={toggleArchitectMode}
              data-testid="chat-room-architect-toggle"
              title={architectMode ? "Architect key active — click to clear" : "Enter Architect key to unleash dual-mode personas"}
              className={`font-mono text-[9px] uppercase tracking-[0.22em] px-2 py-1 border transition-colors ${
                architectMode
                  ? "border-[var(--gold)] text-glow-gold bg-[rgba(255,178,0,0.08)]"
                  : "border-[var(--chrome)] text-[var(--text-3)] hover:text-glow-gold"
              }`}
            >
              {architectMode ? "◉ architect" : "○ public"}
            </button>
            <button
              onClick={openHistory}
              data-testid="chat-room-history"
              className="font-mono text-[9px] uppercase tracking-[0.22em] px-2 py-1 border border-[var(--chrome)] text-[var(--text-3)] hover:text-glow-cyan"
            >
              ⌬ history
            </button>
            <button
              onClick={newSession}
              data-testid="chat-room-new"
              className="font-mono text-[9px] uppercase tracking-[0.22em] px-2 py-1 border border-[var(--cyan)] text-glow-cyan hover:bg-[rgba(0,240,255,0.08)]"
            >
              + new
            </button>
          </div>
        </div>

        {/* Scrollback */}
        <div
          ref={chatScrollRef}
          className="flex-1 overflow-y-auto px-5 py-4 space-y-3"
          data-testid="chat-room-log"
        >
          {messages.length === 0 && (
            <div className="text-[var(--text-3)] font-mono text-xs italic">
              Begin. {activePersona.label.split(" · ")[0]} is listening.
            </div>
          )}
          {messages.map((m, i) => {
            const isUser = m.role === "user";
            const isSys = m.role === "system";
            return (
              <div
                key={`msg-${i}`}
                className={`flex ${isUser ? "justify-end" : "justify-start"}`}
                data-testid={`chat-room-msg-${i}`}
              >
                <div
                  className={`max-w-[78%] px-4 py-2.5 ${
                    isUser
                      ? "bg-[rgba(0,240,255,0.06)] border border-[var(--cyan)]"
                      : isSys
                      ? "bg-[rgba(255,67,67,0.06)] border border-[var(--red)]"
                      : `bg-[rgba(255,255,255,0.02)] border border-[var(--chrome)]`
                  }`}
                >
                  <div
                    className={`font-mono text-[9px] uppercase tracking-[0.22em] mb-1 ${
                      isUser
                        ? "text-glow-cyan"
                        : isSys
                        ? "text-glow-red"
                        : `text-glow-${style.glow}`
                    }`}
                  >
                    {isUser
                      ? "YOU"
                      : isSys
                      ? "SYSTEM"
                      : activePersona.label.split(" · ")[0].toUpperCase()}
                    {m.signed && <span className="ml-2 text-glow-gold">✓ sealed</span>}
                    {m.model && (
                      <span className="ml-2 text-[var(--text-3)] tracking-[0.16em]">
                        · {m.provider}/{m.model}
                      </span>
                    )}
                  </div>
                  <div className="font-mono text-[12.5px] text-[var(--text)] whitespace-pre-wrap leading-relaxed">
                    {isUser || isSys ? m.content : <AutoLink text={m.content} />}
                  </div>
                </div>
              </div>
            );
          })}
          {busy && (
            <div className="flex justify-start">
              <div className="px-4 py-2 border border-[var(--chrome)] bg-[rgba(255,255,255,0.02)]">
                <div className={`font-mono text-[10px] italic text-glow-${style.glow}`}>
                  …{activePersona.label.split(" · ")[0]} is considering…
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
          className="px-4 py-3 border-t border-[var(--chrome)] flex gap-2"
        >
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            disabled={busy}
            rows={2}
            placeholder={`speak to ${activePersona.label.split(" · ")[0]}…  (Enter to send · Shift+Enter for newline)`}
            data-testid="chat-room-input"
            className="flex-1 bg-[var(--void)] border border-[var(--chrome)] focus:border-[var(--cyan)] focus:outline-none text-[var(--text)] font-mono text-[12.5px] px-3 py-2 resize-none"
          />
          <button
            type="submit"
            disabled={busy || !draft.trim()}
            data-testid="chat-room-send"
            className="font-mono text-[11px] uppercase tracking-[0.28em] px-5 py-2 border border-[var(--cyan)] text-glow-cyan hover:bg-[rgba(0,240,255,0.08)] disabled:opacity-40"
          >
            send ▸
          </button>
        </form>
      </main>

      {/* ── History drawer overlay ───────────────────────────────────── */}
      {showHistory && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
          onClick={() => setShowHistory(false)}
          data-testid="chat-room-history-drawer"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-[var(--void-2)] border border-[var(--cyan)] w-full max-w-2xl max-h-[80vh] flex flex-col"
            style={{ boxShadow: "0 0 40px rgba(0,240,255,0.2)" }}
          >
            <div className="p-4 border-b border-[var(--chrome)] flex items-center justify-between">
              <div className="font-display text-lg text-glow-cyan">⌬ Past sessions</div>
              <button
                onClick={() => setShowHistory(false)}
                className="font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 border border-[var(--chrome)] text-[var(--text-3)] hover:text-glow-cyan"
              >
                close ✕
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {sessions.length === 0 && (
                <div className="text-[var(--text-3)] font-mono text-xs italic">
                  No prior sessions on this device yet.
                </div>
              )}
              {sessions.map((s) => (
                <button
                  key={s.id || s.session_id}
                  onClick={() => loadSession(s.id || s.session_id)}
                  className="w-full text-left p-3 border border-[var(--chrome)] hover:border-[var(--cyan)] hover:bg-[rgba(0,240,255,0.04)]"
                  data-testid={`chat-room-session-${s.id || s.session_id}`}
                >
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-glow-cyan">
                    {s.persona || "—"}
                  </div>
                  <div className="font-mono text-[10px] text-[var(--text-2)] mt-1 truncate">
                    {s.title || s.first_message || (s.id || s.session_id || "").slice(0, 24)}
                  </div>
                  <div className="font-mono text-[8px] uppercase tracking-[0.18em] text-[var(--text-3)] mt-1">
                    {s.updated_at || s.created_at || ""}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
